/**
 * Location Service
 * Uses Geolocation API to get GPS coordinates
 */

/**
 * Get current GPS coordinates
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number}>}
 */
export async function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('הדפדפן אינו תומך באיתור מיקום'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        reject(getLocationError(error));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
}

/**
 * Convert GeolocationPositionError to user-friendly Hebrew error
 * @param {GeolocationPositionError} error
 * @returns {Error}
 */
function getLocationError(error) {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new Error('הגישה למיקום נדחתה. נא לאשר גישה למיקום בהגדרות הדפדפן');
    case error.POSITION_UNAVAILABLE:
      return new Error('לא ניתן לקבל את המיקום כרגע. נא לוודא שה-GPS מופעל');
    case error.TIMEOUT:
      return new Error('הזמן לקבלת המיקום פג. נא לנסות שוב');
    default:
      return new Error('שגיאה לא ידועה בקבלת המיקום');
  }
}

/**
 * Check if geolocation is available
 * @returns {boolean}
 */
export function isGeolocationAvailable() {
  return 'geolocation' in navigator;
}

/**
 * Check if location permission is granted
 * @returns {Promise<'granted' | 'denied' | 'prompt'>}
 */
export async function checkLocationPermission() {
  if (!navigator.permissions) {
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'prompt';
  }
}

const locationService = {
  getCurrentPosition,
  isGeolocationAvailable,
  checkLocationPermission,
};

export default locationService;
